import { Button, Flex, Modal } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import usePrompt from "../../../../hooks/use-prompt"

interface DialogProps {
  open: boolean
  saving: boolean
  onKeepEditing: () => void
  onDiscard: () => void
  onSave: () => void
}

/**
 * The three answers to "you have unsaved layout changes".
 *
 * Keep editing is what Esc and the backdrop do too, so a stray keystroke
 * lands on the one choice that loses nothing.
 */
export const UnsavedDraftDialog = ({
  open,
  saving,
  onKeepEditing,
  onDiscard,
  onSave
}: DialogProps) => {
  const { t } = useTranslation()
  return (
    <Modal
      open={open}
      title={t(
        "portfolio.editLayout.unsavedTitle",
        "You have unsaved layout changes"
      )}
      onCancel={onKeepEditing}
      footer={
        <Flex justify="flex-end" gap={8}>
          <Button onClick={onKeepEditing}>
            {t("portfolio.editLayout.keepEditing", "Keep editing")}
          </Button>
          <Button danger onClick={onDiscard}>
            {t("portfolio.editLayout.discard", "Discard")}
          </Button>
          <Button type="primary" loading={saving} onClick={onSave}>
            {t("portfolio.editLayout.saveLayout", "Save layout")}
          </Button>
        </Flex>
      }
    >
      {t(
        "portfolio.editLayout.unsavedBody",
        "Save them, discard them, or keep editing."
      )}
    </Modal>
  )
}

interface BlockerProps {
  active: boolean
  saving: boolean
  onDiscard: () => void
  /** Resolves true once the layout actually persisted. */
  onSave: () => Promise<boolean>
}

/**
 * Blocks a route change away from a dirty draft.
 *
 * Its own component because `useBlocker` throws outside a data router, and
 * `WidgetsDashboard` is mounted under a plain `MemoryRouter` by a dozen specs
 * and by the Portfolio fallback. The caller decides whether this is mounted
 * at all, so the hook is never called where it cannot work.
 */
export const RouteDraftBlocker = ({
  active,
  saving,
  onDiscard,
  onSave
}: BlockerProps) => {
  const blocker = usePrompt(
    ({ currentLocation, nextLocation }) =>
      active && currentLocation.pathname !== nextLocation.pathname
  )

  return (
    <UnsavedDraftDialog
      open={blocker.state === "blocked"}
      saving={saving}
      onKeepEditing={() => blocker.reset?.()}
      onDiscard={() => {
        onDiscard()
        blocker.proceed?.()
      }}
      onSave={() => {
        void onSave().then((ok) => {
          if (ok) blocker.proceed?.()
        })
      }}
    />
  )
}
